import { Node } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';

const key = new PluginKey('signatureBlockRule');

/**
 * The signature box: a visible block in the text that shows where the candidate signs.
 * It can sit anywhere, with text before and after it. Saved as an empty
 * <div data-signature-block="true"></div>; the candidate's page and the signed PDF put
 * the signature there.
 *
 * Every template has exactly one. If it is deleted a fresh one reappears at the end
 * (move it with "Move signature here" or by dragging it, rather than removing it), a
 * second one is dropped, and there is always a paragraph after it so more text can be
 * added below.
 */
function enforce(state) {
  const type = state.schema.nodes.signatureBlock;
  const paragraph = state.schema.nodes.paragraph;
  const found = [];
  state.doc.descendants((node, pos) => {
    if (node.type === type) {
      found.push(pos);
      return false;
    }
    return true;
  });

  const tr = state.tr;
  let changed = false;
  if (found.length === 0) {
    tr.insert(tr.doc.content.size, type.create());
    changed = true;
  } else if (found.length > 1) {
    for (let i = found.length - 1; i >= 1; i -= 1) tr.delete(found[i], found[i] + 1);
    changed = true;
  }
  if (tr.doc.lastChild && tr.doc.lastChild.type === type) {
    tr.insert(tr.doc.content.size, paragraph.create());
    changed = true;
  }
  return changed ? tr : null;
}

export const SignatureBlock = Node.create({
  name: 'signatureBlock',
  group: 'block',
  atom: true,
  selectable: true,
  draggable: true,

  parseHTML() {
    return [{ tag: 'div[data-signature-block]' }];
  },

  renderHTML() {
    return ['div', { 'data-signature-block': 'true' }];
  },

  addProseMirrorPlugins() {
    return [
      new Plugin({
        key,
        appendTransaction: (transactions, _oldState, newState) =>
          transactions.some((t) => t.docChanged) ? enforce(newState) : null,
      }),
    ];
  },

  // The document the editor starts with is not a transaction, so check it once here.
  onCreate() {
    const tr = enforce(this.editor.state);
    if (tr) this.editor.view.dispatch(tr.setMeta('addToHistory', false));
  },
});

export default SignatureBlock;
